/**
 * useMediaImage / useMediaUrl — resolve a displayable URL for one media
 * derivative (A-WP1).
 *
 * Serving path: mint a short-lived HMAC token via `media.mintGetUrl`, build the
 * token-authenticated download URL, then pass it through the single
 * `withImageTransform` sizing seam.
 *
 * Token lifetime: get-URL tokens are one-shot HMAC with a 5-minute TTL
 * (apps/api/src/routes/media/mint-get-url.ts). The token is minted ONCE per
 * input; there is no proactive near-expiry re-mint. That is safe for an image
 * fetched promptly after mount, but lazy-loaded thumbnails (RecipeCard tile,
 * PhotoStrip cell) can scroll into view >5 min after mount, by which point the
 * token has expired and the download 403s. `useMediaImage` therefore exposes an
 * `onImageError` handler that fires a ONE-SHOT re-mint (fresh token) the first
 * time the served URL fails to load, then gives up — closing the TTL window
 * without any risk of a re-mint loop.
 *
 * Rule 8: a mint failure resolves to `null` (the caller degrades to a placeholder
 * or drops the thumb — never a broken-image box) and is logged, not swallowed.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createApiClient } from "@proof/client-core/api";
import {
  createMediaClient,
  type MediaApiSurface,
  type MediaVariant,
} from "@proof/client-core/api/media";
import { mediaDownloadUrl, withImageTransform } from "./imageTransform";

/** One-shot re-mint budget: a single fresh-token retry on the first load error. */
const MAX_REMINTS = 1;

export interface UseMediaUrlOptions {
  /** Target display width in CSS px, forwarded to the transform seam. */
  width?: number;
  /** When false, no mint is attempted (e.g. the derivative is not ready). */
  ready: boolean;
}

export interface MediaImageBinding {
  /** The token-authenticated, transform-sized URL, or `null` while unresolved. */
  url: string | null;
  /**
   * `<img onError>` handler. Fires a ONE-SHOT re-mint (fresh token) the first
   * time the served URL fails to load — the fix for the 5-minute token-TTL
   * window that lazy loading opened. Returns `true` when a re-mint was scheduled
   * (keep the img mounted so it retries the new URL) and `false` once the single
   * re-mint budget is spent (the caller should fall back to its placeholder /
   * emoji tile). The budget resets whenever the input (mediaUuid/variant/width)
   * changes, so a genuinely different image gets its own retry.
   */
  onImageError: () => boolean;
}

/**
 * Resolve a media derivative URL AND a one-shot re-mint error handler.
 *
 * Prefer this over `useMediaUrl` for any `<img>` that can be revealed long after
 * mount (lazy-loaded thumbnails), where the initial token may have expired by
 * the time the browser fetches it.
 */
export function useMediaImage(
  mediaUuid: string | null,
  variant: MediaVariant,
  opts: UseMediaUrlOptions,
): MediaImageBinding {
  const { width, ready } = opts;
  const [url, setUrl] = useState<string | null>(null);

  // The real oRPC client is structurally compatible at runtime; the cast bridges
  // the narrower `fileExt` enum on the generated router type (collections.ts
  // uses the same structural-surface pattern to sidestep router-type variance).
  const media = useMemo(
    () => createMediaClient(createApiClient({ baseUrl: "" }) as unknown as MediaApiSurface),
    [],
  );
  const mediaRef = useRef(media);
  mediaRef.current = media;

  // One-shot re-mint budget, reset whenever the actual input changes (below).
  const remintCountRef = useRef(0);
  // Bumping this re-runs the mint effect WITHOUT changing the input identity —
  // the re-mint trigger. Kept separate from the input deps so an input change
  // (new image) and a re-mint (same image, fresh token) are distinguishable.
  const [remintNonce, setRemintNonce] = useState(0);

  // Reset the re-mint budget on a genuine input change only (NOT on a nonce bump).
  useEffect(() => {
    remintCountRef.current = 0;
  }, [mediaUuid, variant, ready, width]);

  // Mint on input change AND on a re-mint nonce bump. Re-uses the original
  // cancelled-flag staleness guard so an in-flight mint superseded by a newer
  // input (or a newer re-mint) never clobbers the current URL.
  useEffect(() => {
    if (!mediaUuid || !ready) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    async function mint() {
      try {
        const { token } = await mediaRef.current.mintGetUrl(mediaUuid!, variant);
        if (cancelled) return;
        const download = mediaDownloadUrl(mediaUuid!, variant, token);
        setUrl(withImageTransform(download, width !== undefined ? { width } : {}));
      } catch (err) {
        if (cancelled) return;
        console.error({ event: "media_mint_get_url_failed", mediaUuid, variant, err });
        setUrl(null);
      }
    }
    void mint();
    return () => {
      cancelled = true;
    };
  }, [mediaUuid, variant, ready, width, remintNonce]);

  const onImageError = useCallback((): boolean => {
    // Guard against loops: at most one re-mint per input. `>=` (not `===`) is
    // belt-and-braces against any double-fire of the error event.
    if (remintCountRef.current >= MAX_REMINTS) return false;
    remintCountRef.current += 1;
    setRemintNonce((n) => n + 1);
    return true;
  }, []);

  return { url, onImageError };
}

/**
 * Backward-compatible thin wrapper: resolve just the URL string.
 *
 * Retained for the many call sites that render a promptly-fetched image and do
 * not need the re-mint handler (PhotoViewer, EvidencePanel, DashboardHero,
 * VersionThumbStrip). Delegates to `useMediaImage` so the mint logic lives in
 * exactly one place.
 */
export function useMediaUrl(
  mediaUuid: string | null,
  variant: MediaVariant,
  opts: UseMediaUrlOptions,
): string | null {
  return useMediaImage(mediaUuid, variant, opts).url;
}
